import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Folder, FilePlus2, BarChart3, Settings, LogOut,
  PenLine, Hammer, BarChartHorizontal, Users as UsersIcon, ListChecks, Gauge,
  Kanban, TrendingUp, PoundSterling, ShieldCheck, LifeBuoy, Menu, X, Moon, Sun,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings, BrandLogo } from "../context/SettingsContext";
import { useFeatures } from "../context/FeatureContext";
import { useTheme } from "../lib/theme";

/**
 * Sidebar grouped into 3 modules: PLANNING, EXECUTION, REPORTING.
 * Each module has a flat icon to match the Flow brand mark.
 */
const MODULES = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: Gauge,             // flat gauge icon (overview / KPIs)
    items: [
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
    ],
  },
  {
    key: "planning",
    label: "Planning",
    icon: PenLine,           // flat pen-line icon (design / draft)
    items: [
      { to: "/projects", label: "Projects", icon: Folder, feature: "projects" },
      { to: "/pm", label: "Lifecycle Board", icon: Kanban, feature: "projects" },
      { to: "/projects/new", label: "New Project", icon: FilePlus2, feature: "projects" },
    ],
  },
  {
    key: "execution",
    label: "Execution",
    icon: Hammer,            // flat hammer icon (install / build)
    items: [
      { to: "/operations", label: "Operations", icon: ListChecks },
      { to: "/assets", label: "Assets", icon: ShieldCheck, roles: ["admin", "manager", "epc"] },
      { to: "/opex", label: "OPEX", icon: LifeBuoy, roles: ["admin", "manager", "epc"] },
      { to: "/users", label: "Users", icon: UsersIcon, roles: ["admin", "manager"] },
    ],
  },
  {
    key: "reporting",
    label: "Reporting",
    icon: BarChartHorizontal, // flat horizontal bar chart (analytics)
    items: [
      { to: "/analytics", label: "Analytics", icon: BarChart3, feature: "analytics" },
      { to: "/income", label: "Income Ledger", icon: PoundSterling, roles: ["admin", "manager"] },
      { to: "/pm/investor", label: "Reporting Dashboard", icon: TrendingUp },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { canUse } = useFeatures();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [theme, toggleTheme] = useTheme();

  // Item is visible when its role gate passes AND its feature gate passes.
  const canSee = (item) =>
    (!item.roles || item.roles.includes(user?.role)) &&
    (!item.feature || canUse(item.feature));

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      {/* Mobile top bar with hamburger (hidden on lg+) */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-50 h-14 bg-white border-b border-[var(--line)] flex items-center gap-3 px-4" data-testid="mobile-topbar">
        <button onClick={() => setOpen(true)} className="btn-ghost p-1.5" data-testid="mobile-menu-open" aria-label="Open menu">
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <BrandLogo size={24} />
          <span className="font-display font-bold text-sm truncate uppercase tracking-wide">{settings?.org_name || "Flow"}</span>
        </div>
      </header>

      {/* Dark overlay behind the drawer on mobile */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setOpen(false)} data-testid="mobile-overlay" />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-[var(--line)] bg-white flex flex-col transform transition-transform duration-200 lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-[var(--line)] flex items-center gap-3">
          <BrandLogo size={24} />
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-[15px] leading-tight truncate uppercase tracking-wide">
              {settings?.org_name || "Flow"}
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden btn-ghost p-1" data-testid="mobile-menu-close" aria-label="Close menu">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto" data-testid="main-nav">
          {MODULES.map((mod) => {
            const visible = mod.items.filter(canSee);
            if (!visible.length) return null;
            const ModIcon = mod.icon;
            return (
              <div key={mod.key} className="mb-4" data-testid={`module-${mod.key}`}>
                <div className="px-5 pt-2 pb-1.5 flex items-center gap-2">
                  <span
                    className="inline-flex items-center justify-center"
                    style={{
                      width: 22, height: 22,
                      borderRadius: 6,
                      background: "var(--accent-soft)",
                      color: "var(--accent-hover)",
                    }}
                  >
                    <ModIcon size={13} strokeWidth={2} />
                  </span>
                  <span
                    className="font-mono uppercase tracking-[0.14em] text-[10px] font-bold"
                    style={{ color: "var(--ink-2)" }}
                  >
                    {mod.label}
                  </span>
                </div>
                {visible.map(({ to, label, icon: Icon, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon size={16} />
                    {label}
                  </NavLink>
                ))}
              </div>
            );
          })}

          {/* Settings sits below the modules as a utility item */}
          <div className="mt-2 border-t border-[var(--line-soft)] pt-2">
            <NavLink
              to="/settings"
              onClick={() => setOpen(false)}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-settings"
            >
              <Settings size={16} /> Settings
            </NavLink>
          </div>
        </nav>

        <div className="border-t border-[var(--line)] p-4 text-xs">
          <div className="font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="muted truncate text-[11px]" data-testid="current-user-email">{user?.email}</div>
          <div className="label !text-[9px] mt-1">{user?.role}</div>
          <button onClick={toggleTheme} className="mt-3 btn-ghost w-full justify-center" data-testid="theme-toggle"
                  aria-label="Toggle dark mode" title="Switch between light and dark slate theme">
            {theme === "dark" ? <><Sun size={14} /> Light mode</> : <><Moon size={14} /> Dark mode</>}
          </button>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-2 btn-ghost w-full justify-center"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">{children}</main>
    </div>
  );
}
